import React from 'react';
import { VStack, HStack, ScrollView } from '@gluestack-ui/themed';

import FormField from '../../../../common/components/forms/FormField';
import useData from '../../../hooks/useData';
import ObjectIdField from '../../../common/ObjectIdField';
import { useTenantEventKinds } from '../../../../common/context/TenantSettingsStore';
import SettingsGroupCard from '../../../../common/components/forms/SettingsGroupCard';

const ModuleDetailsForm = () => {
  const { data: authorList, isLoading: isAuthorListLoading } = useData({
    objectName: 'users',
    customQueryKey: 'authors',
    filter: { role: 'lecturer' },
  });

  const { eventKinds } = useTenantEventKinds();

  const lecturerChoices = React.useMemo(() => {
    if (!authorList) return [];
    return authorList.map((a) => ({ label: `${a.lastname}, ${a.firstname}`, value: a.id }));
  }, [authorList]);

  return (
    <ScrollView flex={1} bg="$backgroundLight0">
      <VStack space="xl" py="$6">
        <SettingsGroupCard title="Module Information">
          <VStack space="lg">
            <HStack space="lg" alignItems="flex-end">
              <FormField
                name="title"
                label="Module Title"
                type="text"
                placeholder="Introduction to ..."
                fieldWidth="400px"
                rules={{
                  required: 'Module must have a title',
                  minLength: { value: 3, message: 'Title must be at least 3 characters long' },
                  maxLength: { value: 60, message: 'Title must be less than 60 characters long' },
                }}
              />
              <FormField name="isDeprecated" label="Deprecated" type="switch" defaultValue={false} />
            </HStack>
            <HStack space="lg">
              {!isAuthorListLoading && (
                <FormField
                  name="author.id"
                  label="Author"
                  type="select"
                  fieldWidth="280px"
                  readonly={isAuthorListLoading}
                  placeholder={isAuthorListLoading ? 'Loading author list...' : 'Select lecturer as module author'}
                  choices={lecturerChoices}
                  rules={{ required: 'Module must have an author' }}
                />
              )}

              <FormField
                name="metadata.learningHours"
                label="Workload (h)"
                type="text"
                fieldWidth="120px"
                rules={{
                  required: 'Module must have workload (learning hours)',
                  min: { value: 1, message: 'Must be greater than 0' },
                  max: { value: 999, message: 'Must be less than 1000' },
                }}
              />
            </HStack>

            <FormField name="metadata.description" label="Description" type="rte" size="2xs" fieldHeight={200} />
          </VStack>
        </SettingsGroupCard>

        <SettingsGroupCard title="Default Mandates">
          <VStack space="md">
            {!isAuthorListLoading && (
              <>
                <FormField
                  name="mandates.evaluation.id"
                  label="Evaluation (Corrector)"
                  type="select"
                  readonly={isAuthorListLoading}
                  placeholder="Select default corrector"
                  choices={lecturerChoices}
                  horizontal
                  fieldWidth={240}
                />

                {eventKinds.map((kind) => (
                  <FormField
                    key={kind.id}
                    name={`mandates.events.${kind.id}.id`}
                    label={`${kind.label} Responsible`}
                    type="select"
                    readonly={isAuthorListLoading}
                    placeholder="Select responsible lecturer"
                    choices={lecturerChoices}
                    horizontal
                    fieldWidth={240}
                  />
                ))}
              </>
            )}
          </VStack>
        </SettingsGroupCard>

        <ObjectIdField />
      </VStack>
    </ScrollView>
  );
};

export default ModuleDetailsForm;
